import {EventEmitter, Injectable} from '@angular/core';
import {ElectronService} from 'ngx-electron';
import {PleaseWaitService} from '../please-wait/please-wait.service';
import {LoggerService} from '../../../logging/services/logger.service';
import {IPlatformService} from './IPlatformService';
import {TabbingService} from '../../../tabbing/services/tabbing.service';


@Injectable({
  providedIn: 'root'
})
export class NativeService implements IPlatformService {

  private platform: string;
  private arch: string;
  windowMessage: EventEmitter<any> = new EventEmitter<any>();

  windowDetails: {
    windowId: string,
    headless: boolean,
    maximized: boolean,
    isMainWindow: boolean,
  } = undefined;
  windowDetailsFetched = false;

  constructor(private electronService: ElectronService,
              private pleaseWaitService: PleaseWaitService,
              private loggerService: LoggerService,
              private tabbingService: TabbingService){

    if (this.electronService.isElectronApp) {
      this.electronService.ipcRenderer.on('get-platform', (event, args) => {
        this.platform = args.data;
      });
      this.electronService.ipcRenderer.on('get-arch', (event, args) => {
        this.arch = args.data;
      });
      this.electronService.ipcRenderer.send('get-platform');
      this.electronService.ipcRenderer.send('get-arch');
      this.electronService.ipcRenderer.on('window-communication', (event, args) => {
        this.windowMessage.emit(args);
      });
      this.getWindowDetails();
    }
  }

  requestApplicationExit(): void {
    if (this.electronService.isElectronApp) {
      this.electronService.ipcRenderer.send('request-application-exit');
    }
  }

  async getWindowDetails() {
    this.windowDetails = await this.electronService.ipcRenderer.invoke('get-window-details');
    this.windowDetailsFetched = true;
  }

  get isElectronApp(): boolean {
    return this.electronService.isElectronApp;
  }

  get isMac(): boolean {
    return this.electronService.isMacOS;
  }

  get isWindows(): boolean {
    return this.electronService.isWindows;
  }

  get isLinux(): boolean {
    return this.electronService.isLinux;
  }

  get isX86(): boolean {
    return this.electronService.isX86;
  }

  get isX64(): boolean {
    return this.electronService.isX64;
  }

  get isArm(): boolean {
    return this.electronService.isArm;
  }

  get BrowserName(): string {
    return 'Electron Chromium';
  }

  get isAndroid(): boolean {
    return false;
  }

  get isBrowser(): boolean {
    return false;
  }

  get isIOS(): boolean {
    return false;
  }

  get isHeadless(): Promise<boolean> {
    return new Promise(async (resolve) => {
      while (!this.windowDetailsFetched) {
        await new Promise(res => setTimeout(res, 100));
      }
      resolve(this.windowDetails.headless);
    });
  }

  get isMainWindow(): Promise<boolean> {
    return new Promise(async (resolve) => {
      while (!this.windowDetailsFetched) {
        await new Promise(res => setTimeout(res, 100));
      }
      resolve(this.windowDetails.isMainWindow);
    });
  }

  getVersion(): Promise<any> {
    return new Promise((resolve) => {
      if (this.electronService.isElectronApp) {
        this.electronService.ipcRenderer.once('build-info', (event, args) => {
          resolve(args);
        } );
        this.electronService.ipcRenderer.send('build-info');
      } else {
        resolve('');
      }
    } );
  }

  async closeApp() {
    if (this.electronService.isElectronApp) {
      localStorage.removeItem('tabs');
      this.pleaseWaitService.addToQueue();
      // await this.harService.sendHar();
      await this.loggerService.sendMessages();
      this.pleaseWaitService.removeFromQueue();
      this.electronService.ipcRenderer.send('exit', {windowId: this.windowDetails.windowId, force: true});
    }
  }

  /**
   * Minimizes the desktop app
   */
  minimizeApp() {
    if (this.electronService.isElectronApp) {
      this.electronService.ipcRenderer.send('minimize', {windowId: this.windowDetails.windowId});
    }
  }

  /**
   * Maximizes the desktop app
   */
  maximizeApp() {
    if (this.electronService.isElectronApp) {
      this.electronService.ipcRenderer.send('maximize', {windowId: this.windowDetails.windowId});
    }
  }

  reloadApp() {
    if (this.electronService.isElectronApp) {
      this.electronService.ipcRenderer.send('reload', {windowId: this.windowDetails.windowId});
    }
  }

  openHeadlessWindow(url: string) {
    this.electronService.ipcRenderer.send('open-link',
      {url, mode: 'headless'});
  }

  saveObjectToJsonFile(object: object) {
    this.electronService.ipcRenderer.send('save-json',
      {jsonObject: object});
  }

  openNewWindow(url: string): void {
    this.electronService.ipcRenderer.send('open-link',
      {url, mode: 'internal'});
  }

  openInBrowser(url: string): void {
    if (this.electronService.isElectronApp) {
      this.electronService.shell.openExternal(url);
    }
    this.electronService.ipcRenderer.send('open-link',
      {url, mode: 'external'});
  }

  closeCurrentWindow(): void {
    this.electronService.ipcRenderer.send('exit', {windowId: this.windowDetails.windowId});
  }
}
